import { useState, useEffect, useCallback } from "react";
import { useTranslation } from "../../i18n/i18n";
import { doc, updateDoc, deleteDoc, Timestamp } from "firebase/firestore";
import { db } from "../../firebase";
import { getYearMonthDay } from "../../utils/date";
import TransactionForm from "../AddForms/TransactionForm";
import RecurringForm from "../AddForms/RecurringForm";
import { Pencil, Undo2, Save, AlertTriangle, Trash2, X } from "lucide-react";
import { toast } from "react-toastify";
import { useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import Modal from "../Modal/Modal";

const EditEntry = ({
	entry,
	editable,
	onEditToggle,
	onCancel,
	onBack,
	onEntryUpdated,
	onEntryDeleted,
	onSave,
	isRecurring = false,
	isRecurringEdit = false,
}) => {
	const { translate } = useTranslation();
	const navigate = useNavigate();

	const [transactionData, setTransactionData] = useState({});
	const [recurringData, setRecurringData] = useState({});
	const [isLoading, setIsLoading] = useState(false);
	const [showDeleteModal, setShowDeleteModal] = useState(false);
	const [isClosingDeleteModal, setIsClosingDeleteModal] = useState(false);
	const [initialTransactionData, setInitialTransactionData] = useState({});
	const [initialRecurringData, setInitialRecurringData] = useState({});

	// Initialize data from the entry
	useEffect(() => {
		if (entry) {
			const transactionInitialData = {
				type: entry.type || "expense",
				title: entry.title || "",
				amount: entry.amount || 0,
				date: getYearMonthDay(entry.date),
				categoryId: entry.category || "food",
				subCategoryId: entry.subCategory || "",
			};

			setInitialTransactionData(transactionInitialData);
			setTransactionData(transactionInitialData);

			// If this is a recurring transaction, initialize the recurrence data
			if (isRecurring) {
				const recurringInitialData = {
					isRecurring: true,
					recurringFrequency: entry.frequency || "months",
					recurringInterval: entry.interval || 1,
				};
				setInitialRecurringData(recurringInitialData);
				setRecurringData(recurringInitialData);
			}
		}
	}, [entry, isRecurring]);

	const handleCancel = () => {
		// Reset to initial values
		setTransactionData(initialTransactionData);
		setRecurringData(initialRecurringData);
		onCancel?.();
	};

	const handleDelete = async () => {
		setShowDeleteModal(true);
		setIsClosingDeleteModal(false);
	};

	// Trigger modal closing
	const handleCloseDeleteModal = useCallback(() => {
		setIsClosingDeleteModal(true);
		setShowDeleteModal(false);
		// Clean up closing state after animation duration
		setTimeout(() => {
			setIsClosingDeleteModal(false);
		}, 300); // Match the CSS animation duration
	}, []);

	const confirmDelete = async () => {
		setIsLoading(true);
		try {
			const collection = isRecurring ? "recurringTransactions" : "transactions";
			await deleteDoc(doc(db, collection, entry.id));

			const successMessage = isRecurring
				? translate("alert.recurringDeleted")
				: translate("alert.deleteSuccess");

			toast.success(successMessage);
			onEntryDeleted?.(entry.id);

			if (!isRecurring) {
				navigate("/transactions-history");
			}
		} catch (err) {
			console.error("Delete failed:", err);
			const errorMessage = isRecurring
				? translate("error.deleteRecurring")
				: translate("error.deleteTransaction");
			toast.error(errorMessage);
		} finally {
			setIsLoading(false);
			setShowDeleteModal(false);
		}
	};

	const handleSubmit = async e => {
		e.preventDefault();

		if (
			!transactionData.title?.trim() ||
			!transactionData.amount ||
			!transactionData.categoryId
		) {
			toast.warn(translate("alert.missingMandatoryFields"));
			return;
		}

		setIsLoading(true);

		try {
			if (isRecurring && onSave) {
				// For recurring transactions editing:
				// The date from the form is used directly as the new nextDate
				// This allows users to set the exact next occurrence date
				const nextDate = new Date(transactionData.date);

				// Ensure the date is set to start of day to avoid timezone issues
				nextDate.setHours(0, 0, 0, 0);

				const updatedTransaction = {
					...entry,
					title: transactionData.title.trim(),
					amount: parseFloat(transactionData.amount),
					category: transactionData.categoryId,
					subCategory: transactionData.subCategoryId || null,
					type: transactionData.type,
					// Use the form date directly as nextDate (no calculation needed)
					date: Timestamp.fromDate(nextDate),
					frequency: recurringData.recurringFrequency,
					interval: recurringData.recurringInterval,
				};

				await onSave(updatedTransaction);
			} else {
				// For regular transactions - use the date as transaction date
				const transactionDate = new Date(transactionData.date);
				transactionDate.setHours(0, 0, 0, 0);

				const updatedData = {
					title: transactionData.title.trim(),
					amount: parseFloat(transactionData.amount),
					date: Timestamp.fromDate(transactionDate),
					category: transactionData.categoryId,
					subCategory: transactionData.subCategoryId || null,
					type: transactionData.type,
					updatedAt: Timestamp.now(),
				};

				await updateDoc(doc(db, "transactions", entry.id), updatedData);
				toast.success(translate("alert.updateSuccess"));
				onEntryUpdated?.({ ...entry, ...updatedData });
				onCancel?.(); // exit edit mode
			}
		} catch (err) {
			console.error("Update failed:", err);
			toast.error(translate("alert.updateError"));
		} finally {
			setIsLoading(false);
		}
	};

	// Create deletion modal using portal to render at document root level
	// This ensures it appears above any existing modals
	const renderDeleteModal = () => {
		if (!showDeleteModal && !isClosingDeleteModal) return null;

		return createPortal(
			<Modal
				isOpen={showDeleteModal}
				onClose={handleCloseDeleteModal}
				animated={true}
				modalType="delete"
			>
				<div className="reauth-modal-content glass-effect">
					<div className="settings-title-container">
						<AlertTriangle size={20} color="#ff4444" />
						<h2>
							{isRecurring
								? translate("recurring.delete")
								: translate("history.confirmDelete")}
						</h2>
					</div>
					<div className="layout-row design-form">
						<p>
							{isRecurring
								? translate("alert.confirmDeleteRecurring")
								: translate("alert.confirmDeleteEntry")}
						</p>
					</div>
					<div className="ios-buttons">
						<button
							className="ios-button"
							onClick={handleCloseDeleteModal}
							disabled={isLoading}
						>
							<X size={18} />
							{translate("action.cancel")}
						</button>
						<button
							className="ios-button warning"
							onClick={confirmDelete}
							disabled={isLoading}
						>
							<Trash2 size={18} />
							{isLoading
								? translate("action.deleting")
								: translate("action.delete")}
						</button>
					</div>
				</div>
			</Modal>,
			document.body
		);
	};

	return (
		<>
			<form onSubmit={handleSubmit} className="design-form add-page-form">
				<TransactionForm
					initialData={initialTransactionData}
					onFormChange={setTransactionData}
					disabled={isLoading}
					readOnly={!editable}
					showTypeSelector={true}
					isRecurringEdit={isRecurringEdit}
				/>

				{/* Show the recurrence form only if it is a recurring transaction */}
				{isRecurring && (
					<RecurringForm
						initialData={initialRecurringData}
						onRecurringChange={setRecurringData}
						disabled={isLoading || !editable}
						transactionDate={transactionData.date}
						isRecurringEdit={isRecurringEdit}
					/>
				)}

				{/* Footer Buttons */}
				<div className="form-footer ios-buttons">
					{editable ? (
						<>
							<button
								type="button"
								className="ios-button"
								onClick={handleCancel}
								disabled={isLoading}
							>
								<X size={16} />
								{translate("action.cancel")}
							</button>
							<button
								type="button"
								className="ios-button warning"
								onClick={handleDelete}
								disabled={isLoading}
							>
								<Trash2 size={16} />
								{translate("action.delete")}
							</button>
							<button
								type="submit"
								className="ios-button action"
								disabled={isLoading}
							>
								<Save size={16} />
								{isLoading
									? translate("action.saving")
									: translate("action.save")}
							</button>
						</>
					) : (
						<>
							{onBack && (
								<button type="button" className="ios-button" onClick={onBack}>
									<Undo2 size={16} />
									{translate("action.back")}
								</button>
							)}
							{onEditToggle && (
								<button
									type="button"
									className="ios-button action"
									onClick={onEditToggle}
								>
									<Pencil size={16} />
									{translate("action.edit")}
								</button>
							)}
						</>
					)}
				</div>
			</form>

			{/* Render deletion modal with createPortal for full-screen overlay */}
			{renderDeleteModal()}
		</>
	);
};

export default EditEntry;
